// src/products/dataroom/components/Modals/RenameModal.js
import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X, Edit2 } from 'lucide-react';

const RenameModal = ({ isOpen, onClose, item, itemType, onRename }) => {
  const [name, setName] = useState('');
  const [code, setCode] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (isOpen && item) {
      setName(item.name || item.fullName || '');
      setCode(item.code || '');
      setFirstName(item.firstName || '');
      setLastName(item.lastName || '');
      setError(null);
    }
  }, [isOpen, item]);

  const handleSubmit = async () => {
    if (itemType === 'user') {
      if (!firstName.trim()) {
        setError('First name is required');
        return;
      }
    } else {
      if (!name.trim()) {
        setError('Name is required');
        return;
      }
    }

    if ((itemType === 'party' || itemType === 'team') && !code.trim()) {
      setError('Code is required');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      await onRename({ 
        ...item, 
        name: name.trim(),
        code: code.trim().toUpperCase(),
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        fullName: `${firstName.trim()} ${lastName.trim()}`.trim()
      });
      handleClose();
    } catch (err) {
      setError(err.message || 'Failed to rename');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setName('');
    setCode('');
    setFirstName('');
    setLastName('');
    setError(null);
    onClose();
  };

  if (!isOpen || !item) return null;

  const typeConfig = {
    party: { title: 'Rename Party', color: 'purple', icon: '🏢' },
    team: { title: 'Rename Team', color: 'green', icon: '👥' },
    user: { title: 'Rename User', color: 'blue', icon: '👤' }
  };

  const config = typeConfig[itemType] || typeConfig.party;

  return createPortal(
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
        {/* Header */}
        <div className={`bg-gradient-to-r from-${config.color}-600 to-${config.color}-700 px-6 py-4 flex justify-between items-center`}>
          <div className="flex items-center">
            <Edit2 className="w-5 h-5 text-white mr-3" />
            <h2 className="text-lg font-bold text-white">{config.title}</h2>
          </div>
          <button onClick={handleClose} className="text-white hover:text-gray-200">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6 space-y-4">
          {/* Error Message */}
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3">
              <p className="text-sm text-red-800">{error}</p>
            </div>
          )}

          {/* Current Info */}
          <div className="bg-gray-50 rounded-lg p-3">
            <p className="text-xs text-gray-500 mb-1">Current:</p>
            <p className="text-sm font-medium text-gray-900">
              {item.code && <span className="font-mono text-blue-600">{item.code}</span>}
              {item.code && ' - '}
              {item.name || item.fullName || item.email}
            </p>
          </div>

          {/* Code Field (for parties and teams) */}
          {(itemType === 'party' || itemType === 'team') && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Code {itemType === 'party' ? '(3 characters)' : ''}
              </label>
              <input
                type="text"
                value={code}
                onChange={(e) => setCode(e.target.value.toUpperCase())}
                maxLength={itemType === 'party' ? 3 : 10}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 font-mono"
                placeholder={itemType === 'party' ? 'HTT' : 'T01'}
              />
            </div>
          )}

          {/* Name Fields */}
          {itemType === 'user' ? (
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  First Name *
                </label>
                <input
                  type="text"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  maxLength={50}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="John"
                  autoFocus
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Last Name
                </label>
                <input
                  type="text"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  maxLength={50}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Doe"
                />
              </div>
            </div>
          ) : (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Name
              </label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                maxLength={50}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="Enter name"
                autoFocus
              />
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="bg-gray-50 px-6 py-4 flex justify-end space-x-3 border-t">
          <button
            onClick={handleClose}
            disabled={loading}
            className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={loading}
            className={`flex items-center px-6 py-2 bg-${config.color}-600 text-white rounded-lg hover:bg-${config.color}-700 disabled:opacity-50 disabled:cursor-not-allowed`}
          >
            {loading ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                Saving...
              </>
            ) : (
              <>
                <Edit2 className="w-4 h-4 mr-2" />
                Save Changes
              </>
            )}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default RenameModal;

